import React, { RefObject } from 'react'
import { promiseRequestAnimationFrame } from '~/utils/promiseRequestAnimationFrame'
import { VisibleMessage, MessageAnimType } from '../Messages'
import {
  getMessageSize,
  getElByUid,
  getMsgByType,
  getWrapperPaddingBottom,
  getPrevMsg,
} from './utils'

export const animateTypingToMsgCalc = (
  visibleMessages: VisibleMessage[],
  setVisibleMessages: React.Dispatch<React.SetStateAction<VisibleMessage[]>>,
  ref: RefObject<HTMLDivElement>
) => {
  const msg = getMsgByType(visibleMessages, MessageAnimType.TYPING_TO_MSG_CALC)
  const prevMsg = getPrevMsg(visibleMessages)

  if (!msg || !ref.current) {
    return
  }

  // console.log('TYPING_TO_MSG_CALC', msg);

  const [, msgHeight] = getMessageSize(ref, msg.uid)
  const [, typingHeight] = getMessageSize(ref, prevMsg.uid)

  const heightDiff = msgHeight - typingHeight

  const wrapperHeight = ref.current.offsetHeight
  const wrapperParentHeight = (ref.current.parentElement as HTMLDivElement)
    .offsetHeight
  const newWrapperHeight = wrapperHeight + heightDiff

  const move =
    newWrapperHeight > wrapperParentHeight
      ? Math.min(wrapperHeight + heightDiff - wrapperParentHeight, heightDiff)
      : 0

  const wrapperPaddingBottom = getWrapperPaddingBottom(ref)

  const msgMove =
    newWrapperHeight < wrapperParentHeight
      ? Math.max(wrapperHeight + heightDiff - wrapperParentHeight, heightDiff)
      : Math.max(heightDiff - (newWrapperHeight - wrapperParentHeight), 0)

  setVisibleMessages(
    visibleMessages.map(m => {
      if (m.uid == prevMsg.uid) {
        return {
          ...m,
          animType: MessageAnimType.TYPING_TO_MSG_HIDE,
        }
      } else if (m.uid == msg.uid) {
        return {
          ...m,
          animType: MessageAnimType.TYPING_TO_MSG_ENTER,
          bottomShift: wrapperPaddingBottom - msgMove,
          scrollShift: msgMove,
        }
      } else {
        return {
          ...m,
          animType: MessageAnimType.MOVE,
          translate: [0, -move],
        }
      }
    })
  )
}

export const animateTypingToMsgEnter = (
  visibleMessages: VisibleMessage[],
  setVisibleMessages: React.Dispatch<React.SetStateAction<VisibleMessage[]>>,
  setAnimating: React.Dispatch<React.SetStateAction<boolean>>,
  ref: RefObject<HTMLDivElement>,
  scrollToBottomOffset: () => void
) => {
  const msg = getMsgByType(visibleMessages, MessageAnimType.TYPING_TO_MSG_ENTER)
  const prevMsg = getPrevMsg(visibleMessages)

  if (!msg) {
    return
  }

  // console.log('TYPING_TO_MSG_ENTER', msg);

  const scrollShift = msg.scrollShift ? msg.scrollShift : 0

  const el = getElByUid(ref, msg.uid)

  if (!el) {
    return
  }

  const svg = el.querySelector(':scope > svg') as SVGElement
  const rect = el.querySelector(':scope > svg > rect') as SVGRectElement

  if (!svg || !rect) {
    return
  }

  const wrapperStyle = window.getComputedStyle(el)
  const brWidth = parseInt(`${wrapperStyle.borderWidth}`)
  const brRadius = parseInt(`${wrapperStyle.borderRadius}`)

  const [msgWidth, msgHeight] = getMessageSize(ref, msg.uid, false)
  const [typingWidth, typingHeight] = getMessageSize(ref, prevMsg.uid, false)

  const heightDiff = msgHeight - typingHeight

  ;(async () => {
    svg.setAttribute('viewbox', `0 0 ${msgWidth} ${msgHeight}`)
    svg.setAttribute('width', `${msgWidth}px`)
    svg.setAttribute('height', `${msgHeight}px`)

    rect.setAttribute('rx', `${brRadius}`)
    rect.setAttribute('x', `${brWidth}`)
    rect.setAttribute('y', `${heightDiff - scrollShift + brWidth}`)
    rect.setAttribute('width', `${typingWidth - brWidth * 2}`)
    rect.setAttribute('height', `${typingHeight - brWidth * 2}`)

    await promiseRequestAnimationFrame()

    svg.classList.add('is-animate')
    rect.setAttribute('y', `${brWidth}`)
    rect.setAttribute('width', `${msgWidth - brWidth * 2}px`)
    rect.setAttribute('height', `${msgHeight - brWidth * 2}px`)
  })()

  const onEnd = () => {
    setVisibleMessages(
      visibleMessages
        .map(m => ({
          ...m,
          animType: MessageAnimType.NONE,
        }))
        .filter(m => m.uid != prevMsg.uid)
    )

    // console.log('TYPING_TO_MSG_ENTER koniec', msg);

    setAnimating(false)
    scrollToBottomOffset()
    ;(el as HTMLDivElement).removeEventListener('animationend', onEnd)
  }

  ;(el as HTMLDivElement).addEventListener('animationend', onEnd)
}
